import React, { useState } from 'react';
import { useRealTime } from '../../contexts/RealTimeContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  RefreshCw, Clock, Bell, Settings, Check, X, 
  AlertTriangle, Info, AlertCircle
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Switch } from '../ui/switch';

export const RealTimeIndicator = () => {
  const {
    lastRefresh,
    isAutoRefreshEnabled,
    nextRefreshIn,
    alerts,
    unreadAlertCount,
    triggerRefresh,
    toggleAutoRefresh,
    updateRefreshInterval,
    markAlertRead,
    clearAlerts,
  } = useRealTime();

  const [showSettings, setShowSettings] = useState(false);

  const formatCountdown = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error': return <AlertCircle className="w-4 h-4 text-destructive" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-warning" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Auto-refresh indicator */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full text-sm">
        {isAutoRefreshEnabled ? (
          <>
            <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-muted-foreground">
              Next: {formatCountdown(nextRefreshIn)}
            </span>
          </>
        ) : (
          <>
            <div className="w-2 h-2 rounded-full bg-muted-foreground" />
            <span className="text-xs text-muted-foreground">Auto-refresh off</span>
          </>
        )}
      </div>

      {/* Manual refresh button */}
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={triggerRefresh}
        className="h-8 w-8 p-0"
        title="Refresh now"
        data-testid="manual-refresh-btn"
      >
        <RefreshCw className="w-4 h-4" />
      </Button>

      {/* Alerts popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0 relative"
            data-testid="alerts-btn"
          >
            <Bell className="w-4 h-4" />
            {unreadAlertCount > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-destructive"
              >
                {unreadAlertCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="p-3 border-b flex items-center justify-between">
            <h4 className="font-semibold text-sm">Notifications</h4>
            {alerts.length > 0 && (
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={clearAlerts}>
                Clear all
              </Button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No notifications
              </div>
            ) : (
              alerts.map((alert) => (
                <div 
                  key={alert.id}
                  className={`p-3 border-b hover:bg-muted/50 cursor-pointer ${!alert.read ? 'bg-primary/5' : ''}`}
                  onClick={() => markAlertRead(alert.id)}
                >
                  <div className="flex items-start gap-2">
                    {getAlertIcon(alert.type)}
                    <div className="flex-1">
                      <p className="text-sm font-medium">{alert.title}</p>
                      <p className="text-xs text-muted-foreground">{alert.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(alert.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                    {!alert.read && (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Settings popover */}
      <Popover open={showSettings} onOpenChange={setShowSettings}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0"
            data-testid="realtime-settings-btn"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64" align="end">
          <div className="space-y-4">
            <h4 className="font-semibold text-sm">Real-time Settings</h4>
            
            <div className="flex items-center justify-between">
              <span className="text-sm">Auto-refresh</span>
              <Switch 
                checked={isAutoRefreshEnabled} 
                onCheckedChange={toggleAutoRefresh}
                data-testid="auto-refresh-switch"
              />
            </div>

            <div className="space-y-2">
              <span className="text-sm">Refresh Interval</span>
              <div className="flex gap-2">
                {[5, 10, 15, 30].map((mins) => (
                  <Button
                    key={mins}
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => updateRefreshInterval(mins)}
                  >
                    {mins}m
                  </Button>
                ))}
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default RealTimeIndicator;
